import { useEffect, useRef, useState } from "react";
import { Upload, Download, Eye, Stamp, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { StatusBadge, ConfirmDialog } from "@/components/shared";
import { api, notifyErr, downloadFile, fetchBlobUrl, fmtDateTime, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PdfView } from "@/components/PdfView";

export function Viewer({ doc, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    if (doc) fetchBlobUrl(`/documents/${doc.id}/download`).then((x) => { u = x; setUrl(x); }).catch(notifyErr);
    return () => { u && URL.revokeObjectURL(u); setUrl(null); };
  }, [doc]);
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="document-viewer">
        <DialogHeader><DialogTitle className="truncate pr-6">{doc?.filename}</DialogTitle></DialogHeader>
        <div className={`flex-1 min-h-0 bg-zinc-950 rounded-md overflow-auto ${doc?.content_type === "application/pdf" && url ? "p-2" : "grid place-items-center"}`}>
          {!url || !doc ? <span className="text-zinc-500">Caricamento…</span> : doc.content_type === "application/pdf"
            ? <PdfView url={url} /> : <img src={url} alt={doc.filename} className="max-w-full max-h-full object-contain" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function UploadButton({ onFile, accept = "application/pdf,image/jpeg,image/png", children, testid, capture }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept={accept} capture={capture} className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; f && onFile(f); }} />
      <Button variant="outline" onClick={() => ref.current.click()} data-testid={testid}><Upload size={15} className="mr-1" />{children}</Button>
    </>
  );
}

export default function DocumentsPanel({ entityType, entityId, category = "generico", allowStamp, showSides, title, noUpload }) {
  const { can } = useAuth();
  const [docs, setDocs] = useState(null);
  const [view, setView] = useState(null);
  const [notes, setNotes] = useState("");
  const [del, setDel] = useState(null);
  const load = () => api.get("/documents", { params: { entity_type: entityType, entity_id: entityId, category } }).then((r) => setDocs(r.data)).catch(notifyErr);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [entityId, category]);

  const upload = async (file, side) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category); fd.append("entity_type", entityType); fd.append("entity_id", entityId);
    fd.append("notes", notes); if (side) fd.append("side", side);
    try { await api.post("/documents", fd); toast.success("Documento caricato nell'archivio privato"); setNotes(""); load(); } catch (e) { notifyErr(e); }
  };
  const stamp = async (d) => {
    try { await api.post(`/documents/${d.id}/stamped-copy`); toast.success("Copia timbrata creata. L'originale resta invariato."); load(); } catch (e) { notifyErr(e); }
  };

  return (
    <div data-testid={`documents-panel-${category}`}>
      {title && <h3 className="font-display font-semibold mb-3">{title}</h3>}
      {can("documents", "create") && !noUpload && (
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <Input placeholder="Note (opzionale)" value={notes} onChange={(e) => setNotes(e.target.value)} className="max-w-xs h-10" data-testid="doc-notes-input" />
          {showSides ? <>
            <UploadButton onFile={(f) => upload(f, "fronte")} testid="upload-front-btn">Carica fronte</UploadButton>
            <UploadButton onFile={(f) => upload(f, "retro")} testid="upload-back-btn">Carica retro</UploadButton>
          </> : <UploadButton onFile={(f) => upload(f)} testid="upload-doc-btn">Carica documento</UploadButton>}
        </div>
      )}
      <div className="space-y-2">
        {docs && docs.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-800 rounded-lg">Nessun documento caricato</div>}
        {(docs || []).map((d) => (
          <div key={d.id} className="flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/40" data-testid={`doc-row-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{d.filename}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{label(d.category)}{d.side ? ` · ${d.side}` : ""} · caricato il {fmtDateTime(d.uploaded_at)} da {d.uploaded_by}{d.notes ? ` · ${d.notes}` : ""}</div>
            </div>
            <div className="flex flex-wrap gap-1.5 items-center">
              {d.is_stamped ? <StatusBadge text="Copia timbrata" tone="amber" /> : <StatusBadge text="Originale" tone="green" />}
              {d.immutable && <StatusBadge text="Protetto" tone="blue" />}
              <Button size="sm" variant="ghost" onClick={() => setView(d)} data-testid={`view-doc-${d.id}`}><Eye size={15} /></Button>
              <Button size="sm" variant="ghost" onClick={() => downloadFile(`/documents/${d.id}/download`, d.filename)} data-testid={`download-doc-${d.id}`}><Download size={15} /></Button>
              {allowStamp && !d.is_stamped && can("documents", "create") &&
                <Button size="sm" variant="outline" onClick={() => stamp(d)} data-testid={`stamp-doc-${d.id}`}><Stamp size={15} className="mr-1" />Copia timbrata</Button>}
              {can("documents", "delete") && !d.immutable && <Button size="sm" variant="ghost" onClick={() => setDel(d)} data-testid={`delete-doc-${d.id}`}><Trash2 size={15} className="text-red-400" /></Button>}
              {d.immutable && <ShieldCheck size={15} className="text-sky-400" />}
            </div>
          </div>
        ))}
      </div>
      <Viewer doc={view} onClose={() => setView(null)} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Rimuovere il documento?" destructive confirmText="Rimuovi"
        description="Il documento non sarà più visibile nell'archivio." onConfirm={async () => { try { await api.delete(`/documents/${del.id}`); toast.success("Documento rimosso"); load(); } catch (e) { notifyErr(e); } }} />
    </div>
  );
}
